import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileUpload } from '../components/FileUpload';
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { trackJobApplication } from '../utils/analytics';
import { HoneypotField } from '../components/HoneypotField';

const API = '/api';

export const JobApplicationPage = () => {
  const { t, direction } = useLanguage();
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    country: '',
    city: '',
    current_position: '',
    current_company: '',
    linkedin: '',
    cv_url: '',
    cv_file_id: '',
    portfolio_url: '',
    portfolio_file_id: '',
    skills: '',
    languages: '',
    experience_years: '',
    desired_salary: '',
    currency: 'EUR',
    notice_period: '',
    gdpr_consent: false,
    website: '',  // Honeypot field
  });

  useEffect(() => {
    if (jobId) {
      fetchJob();
    } else {
      setLoading(false);
    }
  }, [jobId]);

  const fetchJob = async () => {
    try {
      const response = await fetch(`${API}/jobs/${jobId}`);
      if (response.ok) {
        const data = await response.json();
        setJob(data);
      }
    } catch (error) {
      console.error('Error fetching job:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.gdpr_consent) {
      setError('GDPR consent is required');
      return;
    }
    
    setSubmitting(true);
    setError('');
    
    try {
      const payload = {
        ...formData,
        job_id: jobId,
        skills: formData.skills.split(',').map(s => s.trim()).filter(Boolean),
        languages: formData.languages.split(',').map(l => l.trim()).filter(Boolean),
        experience_years: formData.experience_years ? parseInt(formData.experience_years) : null,
        desired_salary: formData.desired_salary ? parseInt(formData.desired_salary) : null,
      };
      
      const response = await fetch(`${API}/jobs/${jobId}/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        setSuccess(true);
        // Track job application in Google Analytics
        trackJobApplication(jobId, job?.title);
      } else {
        const data = await response.json();
        setError(data.detail || t('forms.error'));
      }
    } catch (error) {
      setError(t('forms.error'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#33AADD]"></div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-[#111827] mb-2">{t('forms.success')}</h2>
            <p className="text-[#6B7280] mb-6">
              Thank you for your application! We will review your profile and get back to you soon.
            </p>
            <Link to="/jobs">
              <Button className="btn-primary">{t('common.back')} to {t('nav.jobs')}</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB]" data-testid="job-application-page">
      {/* Header */}
      <section className="bg-white border-b border-[#E5E7EB] py-8">
        <div className="container-custom">
          <Link 
            to={job ? `/jobs/${jobId}` : '/jobs'} 
            className="inline-flex items-center text-[#6B7280] hover:text-[#33AADD] mb-6"
            data-testid="back-link"
          >
            <ArrowLeft className={`h-4 w-4 me-2 ${direction === 'rtl' ? 'rtl-flip' : ''}`} />
            {t('common.back')}
          </Link>
          
          <h1 className="text-3xl sm:text-4xl font-bold text-[#111827] mb-2">
            {t('jobs.apply')}
          </h1>
          {job && (
            <p className="text-lg text-[#6B7280]">
              {job.title} at {job.company}
            </p>
          )}
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container-custom max-w-3xl">
          <form onSubmit={handleSubmit}>
            {/* Honeypot anti-spam field */}
            <HoneypotField 
              value={formData.website} 
              onChange={(v) => handleChange('website', v)} 
            />
            
            <Card>
              <CardHeader>
                <CardTitle>Personal Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="full_name">{t('forms.fullName')} *</Label>
                    <Input
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => handleChange('full_name', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-full-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('forms.email')} *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-email"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('forms.phone')} *</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-phone"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="country">{t('forms.country')} *</Label>
                    <Input
                      id="country"
                      value={formData.country}
                      onChange={(e) => handleChange('country', e.target.value)}
                      required
                      className="input-focus"
                      data-testid="input-country"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">{t('forms.city')}</Label>
                    <Input
                      id="city"
                      value={formData.city}
                      onChange={(e) => handleChange('city', e.target.value)}
                      className="input-focus"
                      data-testid="input-city"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="linkedin">{t('forms.linkedin')}</Label>
                    <Input
                      id="linkedin"
                      value={formData.linkedin}
                      onChange={(e) => handleChange('linkedin', e.target.value)}
                      placeholder="https://linkedin.com/in/..."
                      className="input-focus"
                      data-testid="input-linkedin"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardHeader>
                <CardTitle>Professional Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="current_position">{t('forms.currentPosition')}</Label>
                    <Input
                      id="current_position"
                      value={formData.current_position}
                      onChange={(e) => handleChange('current_position', e.target.value)}
                      className="input-focus"
                      data-testid="input-current-position"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="current_company">{t('forms.currentCompany')}</Label>
                    <Input
                      id="current_company"
                      value={formData.current_company}
                      onChange={(e) => handleChange('current_company', e.target.value)}
                      className="input-focus"
                      data-testid="input-current-company"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>{t('forms.cv')} *</Label>
                  <FileUpload
                    type="cv"
                    onUploadComplete={(result) => {
                      if (result) {
                        handleChange('cv_file_id', result.file_id);
                        handleChange('cv_url', result.path);
                      } else {
                        handleChange('cv_file_id', '');
                        handleChange('cv_url', '');
                      }
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('forms.portfolio')}</Label>
                  <FileUpload
                    type="portfolio"
                    onUploadComplete={(result) => {
                      if (result) {
                        handleChange('portfolio_file_id', result.file_id);
                        handleChange('portfolio_url', result.path);
                      } else {
                        handleChange('portfolio_file_id', '');
                        handleChange('portfolio_url', '');
                      }
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="skills">{t('forms.skills')}</Label>
                  <Input
                    id="skills"
                    value={formData.skills}
                    onChange={(e) => handleChange('skills', e.target.value)}
                    placeholder="JavaScript, React, Node.js..."
                    className="input-focus"
                    data-testid="input-skills"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="languages">{t('forms.languages')}</Label>
                  <Input
                    id="languages"
                    value={formData.languages}
                    onChange={(e) => handleChange('languages', e.target.value)}
                    placeholder="English, German, Arabic..."
                    className="input-focus"
                    data-testid="input-languages"
                  />
                </div>

                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="experience_years">{t('forms.experienceYears')}</Label>
                    <Input
                      id="experience_years"
                      type="number"
                      min="0"
                      value={formData.experience_years}
                      onChange={(e) => handleChange('experience_years', e.target.value)}
                      className="input-focus"
                      data-testid="input-experience"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="desired_salary">{t('forms.desiredSalary')}</Label>
                    <Input
                      id="desired_salary"
                      type="number"
                      min="0"
                      value={formData.desired_salary}
                      onChange={(e) => handleChange('desired_salary', e.target.value)}
                      className="input-focus"
                      data-testid="input-salary"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="notice_period">{t('forms.noticePeriod')}</Label>
                    <Input
                      id="notice_period"
                      value={formData.notice_period}
                      onChange={(e) => handleChange('notice_period', e.target.value)}
                      placeholder="2 weeks, 1 month..."
                      className="input-focus"
                      data-testid="input-notice"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="mt-6">
              <CardContent className="p-6">
                <label 
                  htmlFor="gdpr_consent" 
                  className="flex items-start gap-3 p-3 -m-3 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors group"
                  data-testid="gdpr-label-wrapper"
                >
                  <Checkbox
                    id="gdpr_consent"
                    checked={formData.gdpr_consent}
                    onCheckedChange={(checked) => handleChange('gdpr_consent', checked)}
                    data-testid="gdpr-checkbox"
                    className="mt-0.5 flex-shrink-0"
                  />
                  <span className="text-sm text-[#6B7280] leading-relaxed group-hover:text-[#374151]">
                    {t('forms.gdprConsent')}
                  </span>
                </label>

                {error && (
                  <div className="flex items-center gap-2 text-red-600 mt-4 p-3 bg-red-50 rounded-lg">
                    <AlertCircle className="h-5 w-5" />
                    <span>{error}</span>
                  </div>
                )}

                <Button 
                  type="submit" 
                  className="w-full btn-primary mt-6" 
                  disabled={submitting}
                  data-testid="submit-application-btn"
                >
                  {submitting ? t('forms.sending') : t('forms.submit')}
                </Button>
              </CardContent>
            </Card>
          </form>
        </div>
      </section>
    </div>
  );
};

export default JobApplicationPage;
